import React, { useCallback, useEffect, useRef, useState } from 'react';
import { m } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
import { testimonialImages } from '../../data/testimonialImages';
export function TestimonialCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  // Cached layout measurement (item width + gap). Reading getBoundingClientRect /
  // getComputedStyle is a layout-forcing operation, so we only do it on mount and
  // on resize — never inside the scroll handler, which fires far more often.
  const stepRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const measureStep = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.firstElementChild) {
      stepRef.current = track?.clientWidth ?? 0;
      return;
    }
    const itemWidth = (track.firstElementChild as HTMLElement).getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(track).columnGap || '0');
    stepRef.current = itemWidth + gap;
  }, []);
  const updateBounds = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setCanScrollPrev(track.scrollLeft > 4);
    setCanScrollNext(track.scrollLeft < max - 4);
    const step = stepRef.current;
    if (step > 0) {
      const index = Math.round(track.scrollLeft / step);
      setActiveIndex(Math.min(Math.max(index, 0), testimonialImages.length - 1));
    }
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    measureStep();
    updateBounds();
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateBounds();
        ticking = false;
      });
    };
    const handleResize = () => {
      measureStep();
      updateBounds();
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    window.addEventListener('resize', handleResize);
    return () => {
      track.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [measureStep, updateBounds]);
  const scrollByStep = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({
      left: direction * stepRef.current,
      behavior: 'smooth'
    });
  };
  const goToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({
      left: index * stepRef.current,
      behavior: 'smooth'
    });
  };
  return <section className="bg-bg-primary py-10 sm:py-14" data-id="element-214" data-mp="15xfeovixycbi">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6" data-id="element-215" data-mp="2m7zu2o5cg0py">
        <m.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-216" data-mp="2harb4lcdfsnn">
          <SectionTag icon="💬" data-id="element-217" data-mp="2os60r3lce5a7">Testimonios reales</SectionTag>
          <h2 className="mt-5 font-heading text-3xl font-bold uppercase leading-tight text-white sm:text-4xl" data-id="element-218" data-mp="2ttd9p5z81aig">
            Así están usando el material otros entrenadores.
          </h2>
        </m.div>
      </div>

      <div className="mx-auto mt-8 max-w-5xl px-3 sm:px-6" data-id="element-219" data-mp="2jvp9w1mrdctf">
        <div ref={trackRef} role="region" aria-roledescription="carousel" aria-label="Testimonios de entrenadores en capturas de mensajes" className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth" data-id="element-220" data-mp="37k8heexzhlkg">
          {testimonialImages.map((testimonial, i) => <div key={testimonial.src} role="group" aria-roledescription="slide" aria-label={`Testimonio ${i + 1} de ${testimonialImages.length}`} className="aspect-[4/5] w-full flex-shrink-0 snap-start sm:w-[calc(50%-0.5rem)]" data-id="element-221" data-mp="32s7a77foal1a" data-mpi={i}>
              <img src={testimonial.src} alt={testimonial.alt} loading="lazy" decoding="async" className="h-full w-full rounded-2xl object-contain" data-id="element-222" data-mp="27n2kwo2vybdr" />
            </div>)}
        </div>

        <div className="mt-5 flex items-center justify-center gap-4" data-id="element-223" data-mp="1tn1l7bycx9zv">
          <button type="button" onClick={() => scrollByStep(-1)} disabled={!canScrollPrev} aria-label="Testimonio anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:border-lime/40 hover:text-lime disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-224" data-mp="2qcistwgwh647">
            <ChevronLeftIcon size={18} data-id="element-225" data-mp="34b8nl5g8ytpe" />
          </button>

          <div className="flex items-center gap-2" data-id="element-226" data-mp="349nrhbeab2pf">
            {testimonialImages.map((testimonial, i) => <button key={testimonial.src} type="button" onClick={() => goToIndex(i)} aria-label={`Ir al testimonio ${i + 1}`} aria-current={activeIndex === i} className={`h-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime ${activeIndex === i ? 'w-6 bg-lime' : 'w-2 bg-white/20 hover:bg-white/40'}`} data-id="element-227" data-mp="qvgzychvhcjr" data-mpi={i} />)}
          </div>

          <button type="button" onClick={() => scrollByStep(1)} disabled={!canScrollNext} aria-label="Siguiente testimonio" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:border-lime/40 hover:text-lime disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-228" data-mp="1jhjm3qtpk039">
            <ChevronRightIcon size={18} data-id="element-229" data-mp="3om4j90wotub6" />
          </button>
        </div>
      </div>
    </section>;
}